import {produce} from 'immer';
import {ActorRefFrom, assign, assertEvent, enqueueActions, setup} from 'xstate';

import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  ConcentrationLayerMachine,
  createConcentrationLayerId,
  createDefaultLayerSettings,
} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {
  ContentsLayerData,
  ContentsLayerMachine,
} from 'samples/plate-maps/contents-layers/machine/ContentsLayerMachine';
import {getNextLayerNumber} from 'samples/plate-maps/layer-machine/sharedLayerActions';
import {
  MetadataLayerMachine,
  createMetadataLayerId,
  MetadataLayerData,
} from 'samples/plate-maps/metadata-layers/machine/MetadataLayerMachine';
import {
  ParameterLayerData,
  ParameterLayerMachine,
} from 'samples/plate-maps/parameter-layers/machine/ParameterLayerMachine';
import {ParameterFieldType, PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {PlateSchema, PlateSize} from 'samples/storables/StorableSchemaConstants';
import {HydratedUnit} from 'units/util';

import {
  getEmptyPlateMapForScratchPlate,
  getNextScratchPlateDesignModalStep,
  getPreviousScratchPlateDesignModalStep,
  removeSchemaTagsFromPlateMap,
  resizePlateMap,
  ScratchPlateDesignModalStep,
  ScratchPlateDesignState,
} from './ScratchPlateDesignModalUtil';

// -- Context ------------------------------------------------------------------

export interface ScratchPlateModalContext {
  // State of configured plate
  name: string | null;
  plateSize: PlateSize | null;
  plateSchema: PlateSchema | null;
  plateMap: PlateMap | null;

  // State of UI
  currentStep: ScratchPlateDesignModalStep;
  selectedWellSchemaFieldIds: Set<string>;
  concentrationLayerMachine: ActorRefFrom<typeof ConcentrationLayerMachine> | null;
  metadataLayerMachine: ActorRefFrom<typeof MetadataLayerMachine> | null;
  parameterLayerMachine: ActorRefFrom<typeof ParameterLayerMachine> | null;
  contentsLayerMachine: ActorRefFrom<typeof ContentsLayerMachine> | null;
}

// -- Input --------------------------------------------------------------------

export type ScratchPlateModalInput = ScratchPlateDesignState | undefined;

// -- Events -------------------------------------------------------------------

export type ScratchPlateModalEvents =
  // State of configured plate
  | {type: 'updateName'; name: string | null}
  | {type: 'updatePlateSize'; plateSize: PlateSize | null}
  | {type: 'updatePlateSchema'; plateSchema: PlateSchema | null}
  | {type: 'updatePlateMap'; plateMap: PlateMap | null}
  | {
      type: 'loadFromTemplate';
      name: string | null;
      plateSize: PlateSize | null;
      plateSchema: PlateSchema | null;
      plateMap: PlateMap | null;
    }

  // State of UI
  | {type: 'nextStep'}
  | {type: 'previousStep'}
  | {type: 'addSelectedSchemaFieldId'; schemaFieldId: string}
  | {type: 'removeSelectedSchemaFieldId'; schemaFieldId: string}
  | {type: 'addConcentrationLayer'}
  | {type: 'removeConcentrationLayer'; layerId: string}
  | {type: 'addMetadataLayer'}
  | {type: 'removeMetadataLayer'; layerId: string}
  | {type: 'updateMetadataLayerType'; layerId: string; layerType: LeafType}
  | {
      type: 'addParameterLayer';
      parameterVersionId: string;
      name: string;
      fieldType: ParameterFieldType;
    }
  | {type: 'removeParameterLayer'; layerId: string}
  | {type: 'addContentsLayer'; layerId: string}
  | {type: 'removeContentsLayer'; layerId: string};

// -- Helpers ------------------------------------------------------------------

function buildInitialConcentrationLayers(
  plateMap: PlateMap | null
): Array<ReturnType<typeof createDefaultLayerSettings>> {
  return Object.values(plateMap?.concentrationLayerInfoById ?? {}).map((info) => {
    const defaults = createDefaultLayerSettings(info.layerId, info.name);
    // Pre-populate the unit and concentration value from the first well that has a non-null value
    // for this layer, so the controls are not blank when loading from a template or editing.
    const firstWellMeasurement =
      plateMap?.wells.find((w) => w.concentrationLayers[info.layerId]?.unit != null)?.concentrationLayers[
        info.layerId
      ] ?? null;
    if (firstWellMeasurement == null) {
      return defaults;
    }
    return {
      ...defaults,
      concentrationUnit: firstWellMeasurement.unit as unknown as HydratedUnit,
    };
  });
}

function buildInitialMetadataLayers(plateMap: PlateMap | null): Array<MetadataLayerData> {
  return Object.values(plateMap?.metadataLayerInfoById ?? {}).map((info) => ({
    layerId: info.layerId,
    name: info.name,
    type: info.type,
  }));
}

function buildInitialParameterLayers(plateMap: PlateMap | null): Array<ParameterLayerData> {
  return Object.values(plateMap?.parameterLayerInfoById ?? {}).map((info) => ({
    layerId: info.layerId,
    name: info.name,
    fieldType: info.fieldType,
  }));
}

function buildInitialContentsLayers(plateMap: PlateMap | null): Array<ContentsLayerData> {
  return Object.values(plateMap?.contentsLayerInfoById ?? {}).map((info) => ({
    layerId: info.layerId,
    name: info.name,
  }));
}

// -- Machine ------------------------------------------------------------------

export const ScratchPlateMachine = setup({
  types: {
    input: {} as ScratchPlateModalInput,
    context: {} as ScratchPlateModalContext,
    events: {} as ScratchPlateModalEvents,
  },
  actions: {
    spawnConcentrationLayerMachine: assign(({context, spawn}) => {
      const concentrationLayerMachine = spawn(ConcentrationLayerMachine, {
        input: {initialLayers: buildInitialConcentrationLayers(context.plateMap), activeLayerId: null},
      });
      return {concentrationLayerMachine};
    }),

    spawnMetadataLayerMachine: assign(({context, spawn}) => {
      const metadataLayerMachine = spawn(MetadataLayerMachine, {
        input: {initialLayers: buildInitialMetadataLayers(context.plateMap), activeLayerId: null},
      });
      return {metadataLayerMachine};
    }),

    spawnParameterLayerMachine: assign(({context, spawn}) => {
      const parameterLayerMachine = spawn(ParameterLayerMachine, {
        input: {initialLayers: buildInitialParameterLayers(context.plateMap), activeLayerId: null},
      });
      return {parameterLayerMachine};
    }),

    spawnContentsLayerMachine: assign(({context, spawn}) => {
      const contentsLayerMachine = spawn(ContentsLayerMachine, {
        input: {initialLayers: buildInitialContentsLayers(context.plateMap), activeLayerId: null},
      });
      return {contentsLayerMachine};
    }),

    updateName: assign(({event}) => {
      assertEvent(event, 'updateName');
      return {name: event.name};
    }),

    updatePlateSize: assign(({context, event}) => {
      assertEvent(event, 'updatePlateSize');
      const {plateSize} = event;

      if (plateSize == null) {
        // Clearing the plate size also clears the schema, plate map, concentration layers, and sources layers
        context.concentrationLayerMachine?.send({type: 'initLayers', layers: []});
        context.metadataLayerMachine?.send({type: 'initLayers', layers: []});
        context.parameterLayerMachine?.send({type: 'initLayers', layers: []});
        context.contentsLayerMachine?.send({type: 'initLayers', layers: []});
        return {plateSize: null, plateSchema: null, plateMap: null, selectedWellSchemaFieldIds: new Set()};
      }

      const [newHeight, newWidth] = plateSize;
      let inferredUpdates: Partial<ScratchPlateModalContext> = {plateSize};

      // Clear the schema if its dimensions no longer match the new size
      if (
        context.plateSchema != null &&
        (context.plateSchema.height !== newHeight || context.plateSchema.width !== newWidth)
      ) {
        inferredUpdates = {...inferredUpdates, plateSchema: null};
      }

      // Initialize or resize the plate map to match the new dimensions
      if (context.plateMap == null) {
        inferredUpdates = {
          ...inferredUpdates,
          plateMap: getEmptyPlateMapForScratchPlate(newHeight, newWidth),
        };
      } else if (
        context.plateMap.size.numRows !== newHeight ||
        context.plateMap.size.numColumns !== newWidth
      ) {
        inferredUpdates = {
          ...inferredUpdates,
          plateMap: resizePlateMap(context.plateMap, newHeight, newWidth),
        };
      }

      return inferredUpdates;
    }),

    updatePlateSchema: assign(({context, event}) => {
      assertEvent(event, 'updatePlateSchema');
      const {plateSchema} = event;

      const currentPlateMap = context.plateMap;

      if (plateSchema == null) {
        // Removing the schema strips all schema tags from the plate map and clears selections
        return {
          plateSchema: null,
          selectedWellSchemaFieldIds: new Set<string>(),
          plateMap: currentPlateMap != null ? removeSchemaTagsFromPlateMap(currentPlateMap) : null,
        };
      } else if (plateSchema.id !== context.plateSchema?.id) {
        // Changing schema clears existing tags and selections — user adds fields explicitly in step 2
        return {
          plateSchema,
          selectedWellSchemaFieldIds: new Set<string>(),
          plateMap: currentPlateMap != null ? removeSchemaTagsFromPlateMap(currentPlateMap) : null,
        };
      }

      return {plateSchema};
    }),

    updatePlateMap: assign(({event}) => {
      assertEvent(event, 'updatePlateMap');
      return {plateMap: event.plateMap};
    }),

    loadFromTemplate: enqueueActions(({context, event, enqueue}) => {
      assertEvent(event, 'loadFromTemplate');
      // Stop the previous concentration and sources layer actors before spawning replacements
      if (context.concentrationLayerMachine != null) {
        enqueue.stopChild(context.concentrationLayerMachine);
      }
      if (context.metadataLayerMachine != null) {
        enqueue.stopChild(context.metadataLayerMachine);
      }
      if (context.parameterLayerMachine != null) {
        enqueue.stopChild(context.parameterLayerMachine);
      }
      if (context.contentsLayerMachine != null) {
        enqueue.stopChild(context.contentsLayerMachine);
      }
      // Atomically replace all plate state from a template without triggering cascading clears
      const {plateMap} = event;
      enqueue.assign(({spawn}) => {
        const concentrationLayerMachine = spawn(ConcentrationLayerMachine, {
          input: {initialLayers: buildInitialConcentrationLayers(plateMap), activeLayerId: null},
        });
        const metadataLayerMachine = spawn(MetadataLayerMachine, {
          input: {initialLayers: buildInitialMetadataLayers(plateMap), activeLayerId: null},
        });
        const parameterLayerMachine = spawn(ParameterLayerMachine, {
          input: {initialLayers: buildInitialParameterLayers(plateMap), activeLayerId: null},
        });
        const contentsLayerMachine = spawn(ContentsLayerMachine, {
          input: {initialLayers: buildInitialContentsLayers(plateMap), activeLayerId: null},
        });
        return {
          name: event.name,
          plateSize: event.plateSize,
          plateSchema: event.plateSchema,
          plateMap,
          concentrationLayerMachine,
          metadataLayerMachine,
          parameterLayerMachine,
          contentsLayerMachine,
          selectedWellSchemaFieldIds: new Set(
            plateMap?.wells.flatMap((well) => Object.keys(well.schemaTags)) ?? []
          ),
        };
      });
    }),

    nextStep: assign(({context}) => ({
      currentStep: getNextScratchPlateDesignModalStep(context.currentStep),
    })),

    previousStep: assign(({context}) => ({
      currentStep: getPreviousScratchPlateDesignModalStep(context.currentStep),
    })),

    addSelectedSchemaFieldId: assign(({context, event}) => {
      assertEvent(event, 'addSelectedSchemaFieldId');
      const newSet = new Set(context.selectedWellSchemaFieldIds);
      newSet.add(event.schemaFieldId);
      return {selectedWellSchemaFieldIds: newSet};
    }),

    removeSelectedSchemaFieldId: assign(({context, event}) => {
      assertEvent(event, 'removeSelectedSchemaFieldId');
      const newSelectedWellSchemaFieldIds = new Set(context.selectedWellSchemaFieldIds);
      newSelectedWellSchemaFieldIds.delete(event.schemaFieldId);
      const {plateMap} = context;

      if (plateMap == null) {
        return {selectedWellSchemaFieldIds: newSelectedWellSchemaFieldIds};
      }

      // Removing a selected schema field should remove the related schema tags from the plate wells
      return {
        selectedWellSchemaFieldIds: newSelectedWellSchemaFieldIds,
        plateMap: {
          ...plateMap,
          wells: plateMap.wells.map((well) => {
            const {[event.schemaFieldId]: _removed, ...remainingTags} = well.schemaTags;
            return {...well, schemaTags: remainingTags};
          }),
        },
      };
    }),

    addConcentrationLayer: assign(({context, event}) => {
      assertEvent(event, 'addConcentrationLayer');
      const {concentrationLayerMachine, plateMap} = context;
      if (concentrationLayerMachine == null || plateMap == null) {
        return {};
      }
      const layers = concentrationLayerMachine.getSnapshot().context.layers;
      const nextNumber = getNextLayerNumber(layers, /^Concentration layer (\d+)$/);
      const newName = `Concentration layer ${nextNumber}`;
      const newLayerId = createConcentrationLayerId();
      concentrationLayerMachine.send({type: 'addLayer', layerId: newLayerId, name: newName});

      // Adding a concentration layer should add the layer to plateMap
      return {
        plateMap: produce(plateMap, (draft) => {
          draft.concentrationLayerInfoById = {
            ...draft.concentrationLayerInfoById,
            [newLayerId]: {layerId: newLayerId, name: newName},
          };
        }),
      };
    }),

    removeConcentrationLayer: assign(({context, event}) => {
      assertEvent(event, 'removeConcentrationLayer');
      const {concentrationLayerMachine, plateMap} = context;
      if (concentrationLayerMachine == null || plateMap == null) {
        return {};
      }
      const {layerId} = event;
      concentrationLayerMachine.send({type: 'removeLayer', layerId});

      return {
        plateMap: produce(plateMap, (draft) => {
          if (draft.concentrationLayerInfoById) {
            delete draft.concentrationLayerInfoById[layerId];
          }
          for (const well of draft.wells) {
            delete well.concentrationLayers[layerId];
          }
        }),
      };
    }),

    addMetadataLayer: assign(({context, event}) => {
      assertEvent(event, 'addMetadataLayer');
      const {metadataLayerMachine, plateMap} = context;
      if (metadataLayerMachine == null || plateMap == null) {
        return {};
      }
      const layers = metadataLayerMachine.getSnapshot().context.layers;
      const nextNumber = getNextLayerNumber(layers, /^Metadata layer (\d+)$/);
      const newName = `Metadata layer ${nextNumber}`;
      const newLayerId = createMetadataLayerId();
      metadataLayerMachine.send({
        type: 'addLayer',
        layerId: newLayerId,
        name: newName,
        layerType: LeafType.TEXT,
      });

      return {
        plateMap: produce(plateMap, (draft) => {
          draft.metadataLayerInfoById = {
            ...draft.metadataLayerInfoById,
            [newLayerId]: {layerId: newLayerId, name: newName, type: LeafType.TEXT},
          };
        }),
      };
    }),

    addContentsLayer: assign(({context, event}) => {
      assertEvent(event, 'addContentsLayer');
      const {contentsLayerMachine, plateMap} = context;
      if (contentsLayerMachine == null || plateMap == null) {
        return {};
      }
      const layers = contentsLayerMachine.getSnapshot().context.layers;
      const nextNumber = getNextLayerNumber(layers, /^Contents layer (\d+)$/);
      const newName = `Contents layer ${nextNumber}`;
      const {layerId} = event;
      contentsLayerMachine.send({type: 'addLayer', layerId, name: newName});

      return {
        plateMap: produce(plateMap, (draft) => {
          draft.contentsLayerInfoById = {
            ...draft.contentsLayerInfoById,
            [layerId]: {layerId, name: newName},
          };
        }),
      };
    }),

    removeMetadataLayer: assign(({context, event}) => {
      assertEvent(event, 'removeMetadataLayer');
      const {metadataLayerMachine, plateMap} = context;
      if (metadataLayerMachine == null || plateMap == null) {
        return {};
      }
      const {layerId} = event;
      metadataLayerMachine.send({type: 'removeLayer', layerId});

      return {
        plateMap: produce(plateMap, (draft) => {
          if (draft.metadataLayerInfoById) {
            delete draft.metadataLayerInfoById[layerId];
          }
          for (const well of draft.wells) {
            if (well.metadataLayers) {
              delete well.metadataLayers[layerId];
            }
          }
        }),
      };
    }),

    updateMetadataLayerType: assign(({context, event}) => {
      assertEvent(event, 'updateMetadataLayerType');
      const {metadataLayerMachine, plateMap} = context;
      if (metadataLayerMachine == null || plateMap == null) {
        return {};
      }
      const {layerId, layerType} = event;
      metadataLayerMachine.send({type: 'updateLayerType', layerId, layerType});

      return {
        plateMap: produce(plateMap, (draft) => {
          if (draft.metadataLayerInfoById?.[layerId]) {
            draft.metadataLayerInfoById[layerId].type = layerType;
          }
          for (const well of draft.wells) {
            if (well.metadataLayers && Object.hasOwn(well.metadataLayers, layerId)) {
              well.metadataLayers[layerId] = null;
            }
          }
        }),
      };
    }),

    addParameterLayer: assign(({context, event}) => {
      assertEvent(event, 'addParameterLayer');
      const {parameterLayerMachine, plateMap} = context;
      if (parameterLayerMachine == null || plateMap == null) {
        return {};
      }
      const {parameterVersionId, name, fieldType} = event;
      // A parameter version may be added at most once; ignore duplicate adds.
      if (plateMap.parameterLayerInfoById?.[parameterVersionId] != null) {
        return {};
      }
      parameterLayerMachine.send({type: 'addLayer', layerId: parameterVersionId, name, fieldType});

      return {
        plateMap: produce(plateMap, (draft) => {
          draft.parameterLayerInfoById = {
            ...draft.parameterLayerInfoById,
            [parameterVersionId]: {layerId: parameterVersionId, name, fieldType},
          };
        }),
      };
    }),

    removeParameterLayer: assign(({context, event}) => {
      assertEvent(event, 'removeParameterLayer');
      const {parameterLayerMachine, plateMap} = context;
      if (parameterLayerMachine == null || plateMap == null) {
        return {};
      }
      const {layerId} = event;
      parameterLayerMachine.send({type: 'removeLayer', layerId});

      return {
        plateMap: produce(plateMap, (draft) => {
          if (draft.parameterLayerInfoById) {
            delete draft.parameterLayerInfoById[layerId];
          }
          for (const well of draft.wells) {
            if (well.parameterLayers) {
              delete well.parameterLayers[layerId];
            }
          }
        }),
      };
    }),

    removeContentsLayer: assign(({context, event}) => {
      assertEvent(event, 'removeContentsLayer');
      const {contentsLayerMachine, plateMap} = context;
      if (contentsLayerMachine == null || plateMap == null) {
        return {};
      }
      const {layerId} = event;
      contentsLayerMachine.send({type: 'removeLayer', layerId});

      return {
        plateMap: produce(plateMap, (draft) => {
          if (draft.contentsLayerInfoById) {
            delete draft.contentsLayerInfoById[layerId];
          }
          for (const well of draft.wells) {
            if (well.contentsLayers) {
              delete well.contentsLayers[layerId];
            }
          }
        }),
      };
    }),
  },
}).createMachine({
  context: ({input}) => ({
    name: input?.name ?? null,
    plateSize: input?.plateSize ?? null,
    plateSchema: input?.plateSchema ?? null,
    plateMap: input?.plateMap ?? null,
    currentStep: ScratchPlateDesignModalStep.DEFINE_SETTINGS,
    selectedWellSchemaFieldIds: new Set(
      input?.plateMap?.wells.flatMap((well) => Object.keys(well.schemaTags)) ?? []
    ),
    concentrationLayerMachine: null,
    metadataLayerMachine: null,
    parameterLayerMachine: null,
    contentsLayerMachine: null,
  }),
  entry: [
    'spawnConcentrationLayerMachine',
    'spawnMetadataLayerMachine',
    'spawnParameterLayerMachine',
    'spawnContentsLayerMachine',
  ],
  on: {
    updateName: {actions: ['updateName']},
    updatePlateSize: {actions: ['updatePlateSize']},
    updatePlateSchema: {actions: ['updatePlateSchema']},
    updatePlateMap: {actions: ['updatePlateMap']},
    loadFromTemplate: {actions: ['loadFromTemplate']},
    addSelectedSchemaFieldId: {actions: ['addSelectedSchemaFieldId']},
    removeSelectedSchemaFieldId: {actions: ['removeSelectedSchemaFieldId']},
    addConcentrationLayer: {actions: ['addConcentrationLayer']},
    removeConcentrationLayer: {actions: ['removeConcentrationLayer']},
    addMetadataLayer: {actions: ['addMetadataLayer']},
    removeMetadataLayer: {actions: ['removeMetadataLayer']},
    updateMetadataLayerType: {actions: ['updateMetadataLayerType']},
    addParameterLayer: {actions: ['addParameterLayer']},
    removeParameterLayer: {actions: ['removeParameterLayer']},
    addContentsLayer: {actions: ['addContentsLayer']},
    removeContentsLayer: {actions: ['removeContentsLayer']},
    nextStep: {actions: ['nextStep']},
    previousStep: {actions: ['previousStep']},
  },
});
